<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import DevicesTab from '@/components/equipment/DevicesTab.vue';
import GroupsTab from '@/components/equipment/GroupsTab.vue';
import SitesTab from '@/components/equipment/SitesTab.vue';
import { useIsSystemAdmin } from '@/composables/useIsSystemAdmin';
import '@/components/equipment/equipment-ui.css';

const TABS = [
  { key: 'sites', icon: 'pi-map-marker' },
  { key: 'devices', icon: 'pi-desktop' },
  { key: 'groups', icon: 'pi-sitemap' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

const { t } = useI18n({ useScope: 'global' });
const route = useRoute();
const router = useRouter();
const { isSystemAdmin } = useIsSystemAdmin();

// L'onglet vit dans l'URL : un rechargement ou un lien partagé retombe sur le même onglet.
const tab = computed<TabKey>(() => {
  const asked = route.query.tab;
  return TABS.some((item) => item.key === asked) ? (asked as TabKey) : 'sites';
});
const select = (key: TabKey) => router.replace({ query: { tab: key } });

// Le bouton de création est dans l'en-tête, la fenêtre qu'il ouvre est celle de l'onglet affiché.
const creating = ref(false);
watch(tab, () => (creating.value = false));
</script>

<template>
  <div class="eqp">
    <header class="eqp__head">
      <div class="eqp__titles">
        <h1 class="eqp__title">{{ t('equipment.title') }}</h1>
        <p class="eqp__subtitle">{{ t('equipment.subtitle') }}</p>
      </div>
      <button
        v-if="isSystemAdmin"
        type="button"
        class="eq-btn eq-btn--primary eqp__create"
        @click="creating = true"
      >
        <i class="pi pi-plus" /> {{ t(`equipment.${tab}.create`) }}
      </button>
    </header>

    <nav class="eqp__tabs" role="tablist">
      <button
        v-for="item in TABS"
        :key="item.key"
        type="button"
        role="tab"
        class="eqp__tab"
        :class="{ 'eqp__tab--active': tab === item.key }"
        :aria-selected="tab === item.key"
        @click="select(item.key)"
      >
        <i class="pi" :class="item.icon" />
        {{ t(`equipment.tabs.${item.key}`) }}
      </button>
    </nav>

    <SitesTab v-if="tab === 'sites'" v-model:creating="creating" />
    <DevicesTab v-else-if="tab === 'devices'" v-model:creating="creating" />
    <GroupsTab v-else v-model:creating="creating" />
  </div>
</template>

<style scoped>
.eqp {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 88rem;
  padding: 2.5rem 3rem;
  box-sizing: border-box;
}
@media (max-width: 1023px) {
  .eqp {
    padding: 1.5rem;
  }
}
.eqp__head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
}
.eqp__title {
  margin: 0 0 0.2rem;
  font-size: 1.75rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--nxt-on-surface);
}
.eqp__subtitle {
  margin: 0;
  font-size: 0.9rem;
  color: var(--nxt-on-surface-muted);
}
.eqp__create {
  height: 2.75rem;
  padding: 0 1.25rem;
  border-radius: 999px;
}
.eqp__tabs {
  display: inline-flex;
  align-self: flex-start;
  gap: 0.25rem;
  max-width: 100%;
  padding: 0.3rem;
  border-radius: 999px;
  background: var(--nxt-surface-high);
  overflow-x: auto;
}
.eqp__tab {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 1.1rem;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--nxt-on-surface-muted);
  font-size: 0.875rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s;
}
.eqp__tab:hover:not(.eqp__tab--active) {
  color: var(--nxt-on-surface);
}
.eqp__tab--active {
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
}
</style>
